import React, { useState } from 'react';
import { HiOutlineArrowRight, HiOutlineCheck } from 'react-icons/hi2';
import cloudApi from '../../services/cloudApi';
import McpTokenSetup from '../../components/McpTokenSetup';
import { CursorMark, HermesMark, OpenClawMark } from './connectClients';

// ============================================================================
// The token half of the connect wizard: create an access token, then copy the
// one snippet that matches your app. The sign-in halves (Claude, ChatGPT) need
// none of this, so they live in separate panels rather than one branching
// component.
//
// The token-created step is McpTokenSetup — the very same component the
// Integrations modal uses — so the two surfaces give identical instructions.
// What's local here is the funnel framing: one token, sensible defaults, and an
// escape hatch for the apps that don't need a token at all.
// ============================================================================

// The far side of the handshake — a sample of the apps this token is for. Its
// own list rather than a slice of MCP_AGENT_TILES, and deliberately all
// token-based: Claude and ChatGPT sign in instead (ChatGPT has no way to send a
// token at all), so showing them here would advertise the wrong flow.
const HANDSHAKE_MARKS = [
  { name: 'Cursor', Mark: CursorMark },
  { name: 'OpenClaw', Mark: OpenClawMark },
  { name: 'Hermes', Mark: HermesMark },
];

const ACCESS_LEVELS = [
  {
    value: 'full',
    title: 'Full access',
    description: 'Scrape, run automations and clean tables — what most people want',
  },
  {
    value: 'read_only',
    title: 'Read-only',
    description: 'Analyze existing tables and check runs. Cannot spend credits',
  },
];

const ConnectMcpPanel = ({ mcpUrl, oauthAvailable, onPlanRequired, onSwitchStory }) => {
  const [name, setName] = useState('My AI agent');
  const [accessLevel, setAccessLevel] = useState('full');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const result = await cloudApi.createMcpToken({ name: name.trim(), accessLevel });
      setCreated(result);
    } catch (err) {
      console.error('Failed to create MCP token:', err);
      if (err.code === 'PLAN_REQUIRED' || err.statusCode === 402) {
        // The wizard swaps in the paid-plan upsell instead.
        onPlanRequired?.();
      } else {
        setError(err.message || 'Failed to create token.');
      }
    } finally {
      setCreating(false);
    }
  };

  // ── Created ─────────────────────────────────────────────────────────────
  // Same panel the Integrations modal shows, tabs and all — one implementation,
  // so the instructions for a given client are identical wherever you minted
  // the token. Claude keeps its tab even though this wizard steers Claude users
  // to sign-in beforehand: it's the fallback for anyone who made a token first.
  if (created) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/20 dark:ring-emerald-400/20 flex items-center justify-center">
            <HiOutlineCheck className="w-5 h-5 text-emerald-500 dark:text-emerald-400" strokeWidth={2.5} />
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">Token created</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">One last step — connect your app below</p>
          </div>
        </div>

        <McpTokenSetup token={created.token} mcpUrl={mcpUrl} oauthAvailable={oauthAvailable} />
      </div>
    );
  }

  // ── Create ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="text-center mb-6">
        {/* Branded handshake — us, linked to whichever agent they're connecting.
            Mirrors the Claude panel's pairing so the two feel like one product. */}
        <div className="relative flex items-center justify-center gap-3 mb-5">
          <div className="absolute w-40 h-16 bg-cyan-400/20 dark:bg-cyan-400/15 blur-2xl rounded-full" aria-hidden="true" />
          <div className="relative w-12 h-12 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20 shrink-0">
            <div className="w-full h-full bg-white rounded-[10px] p-1.5 flex items-center justify-center">
              <img src="/logo-128.png" alt="Ultimate Web Scraper logo" className="w-full h-full" />
            </div>
          </div>
          <span className="relative flex items-center" aria-hidden="true">
            <span className="w-3 h-px bg-gradient-to-r from-transparent to-cyan-400/60" />
            <HiOutlineArrowRight className="w-4 h-4 text-cyan-500/80 dark:text-cyan-400/70" />
            <span className="w-3 h-px bg-gradient-to-l from-transparent to-cyan-400/60" />
          </span>
          {/* The far side of the handshake: the apps this token is for, in
              their own marks. */}
          <span className="relative flex -space-x-2 shrink-0">
            {HANDSHAKE_MARKS.map(({ name, Mark }) => (
              <Mark key={name} className="w-11 h-11 rounded-xl ring-2 ring-white dark:ring-gray-900 shadow-lg shadow-black/10" />
            ))}
          </span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mb-2">
          Create your access token
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-md mx-auto">
          One token, pasted into your AI app. That&rsquo;s the whole setup.
        </p>
      </div>

      <div className="max-w-lg mx-auto space-y-5">
        <div>
          <label htmlFor="mcp-token-name" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            Name it after the app
          </label>
          <input
            id="mcp-token-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="e.g. OpenClaw"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            className="w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2.5 rounded-lg text-sm
                       border border-gray-300 dark:border-gray-600/50
                       hover:border-gray-400 dark:hover:border-gray-500/50
                       focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20 focus:outline-none
                       placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
          />
          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">One token per app keeps revoking surgical.</p>
        </div>

        <div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Access level</span>
          <div className="space-y-2">
            {ACCESS_LEVELS.map((level) => {
              const selected = accessLevel === level.value;
              return (
                <button
                  key={level.value}
                  onClick={() => setAccessLevel(level.value)}
                  className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
                    selected
                      ? 'bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-400/70 dark:border-cyan-400/50 shadow-sm shadow-cyan-500/10'
                      : 'bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60'
                  }`}
                >
                  <span className={`absolute left-3.5 top-4 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selected ? 'border-cyan-500 dark:border-cyan-400' : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {selected && <span className="w-2 h-2 rounded-full bg-cyan-500 dark:bg-cyan-400" />}
                  </span>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">{level.title}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{level.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <button
          onClick={handleCreate}
          disabled={creating || !name.trim()}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-lg shadow-cyan-500/25 hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:shadow-none disabled:hover:scale-100 disabled:cursor-not-allowed"
        >
          {creating ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Creating…
            </>
          ) : (
            'Create access token'
          )}
        </button>

        {/* Claude and ChatGPT need no token at all — and ChatGPT can't even send
            one. Say so before they make one they don't need, rather than after. */}
        {onSwitchStory && (
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
            Connecting Claude or ChatGPT? They sign in instead — no token needed.{' '}
            <button
              onClick={() => onSwitchStory('claude')}
              className="font-medium text-cyan-600 dark:text-cyan-400 hover:underline"
            >
              Set up Claude →
            </button>
            <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
            <button
              onClick={() => onSwitchStory('chatgpt')}
              className="font-medium text-cyan-600 dark:text-cyan-400 hover:underline"
            >
              Set up ChatGPT →
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default ConnectMcpPanel;
